import type {
  AutobuildQuotaDecision,
  AutobuildSessionQuotaAccount,
  AutobuildSessionQuotaSnapshot,
  AiQuotaWindow,
} from '@a7y/types';
import { formatQuotaPercent } from './quota-display';
import { fmtLocal } from './shared';

/**
 * What a null `rung` means TODAY (A7Y-3079).
 *
 * It used to mean "outside the admission policy's account order", and this file
 * said exactly that — a re-expression A7Y-2821 made when `tpf` stopped being an
 * unbounded deep reserve. A7Y-3054 invalidated it: the sort view
 * (`ACCOUNT_POLICY_ORDER`) and the rung view (`IMPLEMENTATION_ORDER`) are now two
 * derived views of one policy array, and a null rung means the account is in the
 * order but hosts no implementation. The old wording is not merely stale, it is
 * FALSE for the only account that reaches it: codex is ranked fourth by policy
 * and carries a real 85% ceiling, which this very card renders beside the label.
 *
 * The API can render no counter-example. `parseQuotaSnapshotMessage` admits only
 * the accounts named in `ACCOUNT_POLICY_ORDER`, so "not in the admission order"
 * is unreachable by construction; a null rung is review-only and nothing else.
 */
const NO_RUNG_LABEL = 'no implementation rung';
const NO_RUNG_TITLE =
  'This account is ranked in the admission policy account order and has its own ceiling, ' +
  'but it hosts no implementation — it reviews rather than builds.';

/**
 * How each verdict presents. `selected` is the only one that earns colour —
 * everything else stays deliberately quiet so the eye lands on the account
 * that ran without having to read anything first.
 */
const DECISION_META: Record<
  AutobuildQuotaDecision,
  { label: string; chip: string; card: string }
> = {
  selected: {
    label: 'Selected',
    chip: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
    card: 'border-emerald-500 dark:border-emerald-500',
  },
  // The Claude account that ORCHESTRATES while implementation runs elsewhere
  // (codex). Judged against the 85% driver boundary, never a rung ceiling.
  driver: {
    label: 'Driver',
    chip: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300',
    card: 'border-blue-400 dark:border-blue-600',
  },
  // A7Y-3079. A ROLE, like `selected` and `driver` — not a pass-over reason.
  // Neutral on purpose: the design intent above is that `selected` is the only
  // verdict that earns colour, and a role that never runs anything must not
  // compete with the account that did.
  'review-only': {
    label: 'Review-only',
    chip: 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400',
    card: 'border-zinc-200 dark:border-zinc-800',
  },
  'over-ceiling': {
    label: 'Over ceiling',
    chip: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
    card: 'border-zinc-200 dark:border-zinc-800',
  },
  'not-reached': {
    label: 'Not reached',
    chip: 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400',
    card: 'border-zinc-200 dark:border-zinc-800',
  },
  unavailable: {
    label: 'Unavailable',
    chip: 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400',
    card: 'border-zinc-200 dark:border-zinc-800',
  },
};

function QuotaFigure({
  value,
  ceilingPct,
}: {
  value: AiQuotaWindow | null;
  ceilingPct?: number | null;
}) {
  if (!value) return <span className="text-zinc-400">Not reported</span>;
  // The ceiling is what the waterfall actually tested this figure against, so
  // colour the comparison inline rather than making the reader hold it in mind.
  const over = ceilingPct != null && value.usedPercent >= ceilingPct;
  return (
    <span
      className={`font-medium tabular-nums ${
        over ? 'text-amber-700 dark:text-amber-400' : 'text-zinc-900 dark:text-zinc-100'
      }`}
    >
      {formatQuotaPercent(value.usedPercent)}
    </span>
  );
}

/**
 * Before and after in ONE panel, as two value columns against shared row
 * labels. Two separate cards repeated "Session"/"Weekly" and forced the eye to
 * jump between boxes to compare the numbers the panel exists to compare.
 *
 * The figures are shown plain. An earlier version appended a computed movement
 * ("+100 pts") beside the after-value, which was noise at any width and wrapped
 * onto its own line once the cards went four across — two columns of numbers
 * side by side already state the movement.
 *
 * Accounts that were never selected have no `after`, and rendering an empty
 * second column for them would imply missing data rather than data that was
 * never going to exist. They collapse to a single column headed "At session
 * start".
 */
function QuotaComparison({
  before,
  after,
  ceilingPct,
}: {
  before: AutobuildSessionQuotaSnapshot | null;
  after: AutobuildSessionQuotaSnapshot | null;
  ceilingPct?: number | null;
}) {
  const cols = after ? 2 : 1;
  const grid = cols === 2 ? 'grid-cols-[auto_1fr_1fr]' : 'grid-cols-[auto_1fr]';

  if (!before && !after) {
    return (
      <div className="rounded-md bg-zinc-50 p-3 text-xs text-zinc-400 dark:bg-zinc-950/50">
        Not recorded
      </div>
    );
  }

  const head = (label: string, snap: AutobuildSessionQuotaSnapshot | null) => (
    <div className="text-right">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">{label}</div>
      {snap ? (
        <div className="text-[10px] text-zinc-400" title={snap.observedAt}>
          {fmtLocal(snap.observedAt)}
        </div>
      ) : null}
    </div>
  );

  // A probe error replaces that column's figures; the other column still reads.
  const cell = (snap: AutobuildSessionQuotaSnapshot | null, key: 'session' | 'weekly') =>
    snap?.error ? (
      <span className="text-right text-[10px] text-amber-700 dark:text-amber-300">probe failed</span>
    ) : (
      <span className="text-right">
        <QuotaFigure
          value={snap?.[key] ?? null}
          ceilingPct={key === 'weekly' ? ceilingPct : undefined}
        />
      </span>
    );

  return (
    <div className="rounded-md bg-zinc-50 p-3 dark:bg-zinc-950/50">
      <div className={`grid ${grid} items-baseline gap-x-4 gap-y-1.5 text-xs`}>
        <div />
        {head(after ? 'Before' : 'At session start', before)}
        {after ? head('After', after) : null}

        <span className="text-zinc-500">Session</span>
        {cell(before, 'session')}
        {after ? cell(after, 'session') : null}

        <span className="text-zinc-500">Weekly</span>
        {cell(before, 'weekly')}
        {after ? cell(after, 'weekly') : null}
      </div>
    </div>
  );
}

/**
 * The waterfall stated once, in plain language, above the cards. Rendered from
 * the data rather than hardcoded, so a rung added to the policy's account order
 * appears here without anyone remembering to edit a sentence.
 *
 * THE "DEEP RESERVE" CLAUSE IS GONE, NOT BROKEN (A7Y-2821, cross-package finding
 * §10.1). It used to find its account with `accounts.find(a => a.rung == null)`,
 * which worked only because `tpf` carried a null rung expressing "eligible at any
 * utilization". `POLICY` has no reserve concept — that branch was deleted in
 * commit 1 — so `tpf` is now an ordinary lane with an ordinary ceiling and the
 * lookup silently returned nothing. Re-introducing a null rung to make the card
 * render again would assert a regime the policy no longer has, so the clause is
 * re-expressed against what a null rung means TODAY — which is `NO_RUNG_LABEL`'s
 * comment above, not a repeat of the reasoning here (A7Y-3079).
 */
function WaterfallSummary({ accounts }: { accounts: AutobuildSessionQuotaAccount[] }) {
  const rungs = accounts
    .filter((a) => a.rung != null)
    .sort((a, b) => (a.rung ?? 0) - (b.rung ?? 0));
  const unranked = accounts.filter((a) => a.rung == null);
  const winner = accounts.find((a) => a.decision === 'selected');
  if (rungs.length === 0 && unranked.length === 0) return null;

  const nameClass = (a: AutobuildSessionQuotaAccount) =>
    a.decision === 'selected'
      ? 'font-semibold text-emerald-700 dark:text-emerald-400'
      : a.decision === 'driver'
        ? 'font-medium text-blue-700 dark:text-blue-400'
        : a.decision === 'over-ceiling'
          ? 'text-zinc-400 line-through'
          : 'text-zinc-400';

  return (
    <div className="border-b border-zinc-100 px-3 py-2.5 text-xs text-zinc-500 dark:border-zinc-800">
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
        {rungs.map((a, i) => (
          <span key={a.account} className="inline-flex items-center gap-1.5">
            {i > 0 ? <span className="text-zinc-300 dark:text-zinc-700">→</span> : null}
            <span className={nameClass(a)}>{a.account}</span>
          </span>
        ))}
        {unranked.map((account) => (
          <span key={account.account} className="inline-flex items-center gap-1.5">
            <span className="text-zinc-300 dark:text-zinc-700">·</span>
            <span className={nameClass(account)}>{account.account}</span>
            <span className="text-[10px] text-zinc-400" title={NO_RUNG_TITLE}>
              ({NO_RUNG_LABEL})
            </span>
          </span>
        ))}
      </div>
      <p className="mt-1.5 leading-relaxed">
        Tried in order; the first account under its ceiling implements.{' '}
        {winner ? (
          <>
            Here that was{' '}
            <span className="font-medium text-zinc-700 dark:text-zinc-300">{winner.label}</span>
            {winner.note ? ` — ${winner.note}.` : '.'}
            {(() => {
              const drv = accounts.find((a) => a.decision === 'driver');
              return drv ? (
                <>
                  {' '}
                  <span className="font-medium text-blue-700 dark:text-blue-400">{drv.label}</span>{' '}
                  drove — orchestration, review and landing run on the driver even when
                  implementation is elsewhere.
                </>
              ) : null;
            })()}
          </>
        ) : (
          'No account is recorded as selected for this session.'
        )}
      </p>
    </div>
  );
}

export function SessionQuotaUsage({ accounts }: { accounts: AutobuildSessionQuotaAccount[] }) {
  return (
    <section className="rounded-lg border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">
      <div className="border-b border-zinc-100 px-3 py-2 text-[11px] uppercase tracking-wide text-zinc-400 dark:border-zinc-800">
        Coding accounts considered
      </div>
      {accounts.length === 0 ? (
        <div className="px-3 py-4 text-xs text-zinc-500">
          Account quota was not recorded for this session.
        </div>
      ) : (
        <>
          <WaterfallSummary accounts={accounts} />
          {/* Four across on a wide screen. A7Y-3797: this column count is a
              fixed layout choice, not one derived from the fleet — a waterfall
              carrying more accounts than there are columns wraps to a second
              row. Steps down through 2 rather than jumping straight to 1 — at
              tablet widths a 4-up card is narrower than its own quota table. */}
          <div className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-2 xl:grid-cols-4">
            {accounts.map((account) => {
              const meta = DECISION_META[account.decision] ?? DECISION_META['not-reached'];
              const isSelected = account.decision === 'selected';
              return (
                <div
                  key={`${account.provider}-${account.account}`}
                  className={`rounded-lg border p-3 ${meta.card} ${
                    isSelected ? 'bg-emerald-50/40 dark:bg-emerald-950/10' : ''
                  }`}
                >
                  <div className="mb-3 flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                        {account.label}
                      </div>
                      <div className="text-[11px] uppercase tracking-wide text-zinc-400">
                        {account.provider}
                        {account.rung != null ? ` · rung ${account.rung}` : ` · ${NO_RUNG_LABEL}`}
                        {account.ceilingPct != null ? ` · ceiling ${account.ceilingPct}%` : ''}
                      </div>
                    </div>
                    <span
                      className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${meta.chip}`}
                    >
                      {meta.label}
                    </span>
                  </div>
                  {account.note ? (
                    <p className="mb-3 text-[11px] leading-relaxed text-zinc-500 dark:text-zinc-400">
                      {account.note}
                    </p>
                  ) : null}
                  <QuotaComparison
                    before={account.before}
                    after={isSelected ? account.after : null}
                    ceilingPct={account.ceilingPct}
                  />
                </div>
              );
            })}
          </div>
          {/* A7Y-2821 finding §10.2, said on screen rather than only in a code
              comment. The ceilings above come from the CURRENT policy, because
              no session ever recorded the ceiling it was actually judged
              against — so for a session that ran under an earlier regime this
              panel is not a faithful record of what was enforced. Stating it is
              the honest interim; the durable fix stamps the effective ceiling at
              run time, which is a change to what the session records. */}
          <p className="border-t border-zinc-100 px-3 py-2 text-[10px] leading-relaxed text-zinc-400 dark:border-zinc-800">
            Ceilings shown are the current admission policy&apos;s. Sessions that ran before the
            policy last changed were judged against different numbers, and none of them recorded
            the ceiling in force at the time. Rungs come from the current implementation order in
            the same way: an account that is review-only today shows no rung, including on an older
            session where it did implement.
          </p>
        </>
      )}
    </section>
  );
}
